import { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { useAuth } from "../lib/AuthContext";
import { useBadgeToast } from "./BadgeToast";
import { analyzeMeal } from "../lib/gemini";
import { getUserStats, recordMealAnalysis, checkAndAwardBadges, checkLowGiChampion } from "../lib/userStats";
import type { DietaryPreferences } from "../lib/gemini";
import { BackIcon } from "./Icons";

interface PlateFood {
  id: string;
  name: string;
  gi: number;
  giLevel: "low" | "medium" | "high";
  category: "protein" | "carb" | "vegetable" | "fat" | "fruit" | "dairy";
  emoji: string;
}

const FOOD_OPTIONS: PlateFood[] = [
  // Proteins
  { id: "chicken", name: "Chicken Breast", gi: 0, giLevel: "low", category: "protein", emoji: "🍗" },
  { id: "salmon", name: "Salmon", gi: 0, giLevel: "low", category: "protein", emoji: "🐟" },
  { id: "eggs", name: "Eggs", gi: 0, giLevel: "low", category: "protein", emoji: "🥚" },
  { id: "tofu", name: "Tofu", gi: 15, giLevel: "low", category: "protein", emoji: "🧈" },
  { id: "beef", name: "Lean Beef", gi: 0, giLevel: "low", category: "protein", emoji: "🥩" },

  // Carbs
  { id: "rice", name: "White Rice", gi: 73, giLevel: "high", category: "carb", emoji: "🍚" },
  { id: "basmati", name: "Basmati Rice", gi: 58, giLevel: "medium", category: "carb", emoji: "🍚" },
  { id: "quinoa", name: "Quinoa", gi: 53, giLevel: "low", category: "carb", emoji: "🌾" },
  { id: "pasta", name: "Pasta", gi: 55, giLevel: "low", category: "carb", emoji: "🍝" },
  { id: "bread", name: "White Bread", gi: 75, giLevel: "high", category: "carb", emoji: "🍞" },
  { id: "sourdough", name: "Sourdough", gi: 54, giLevel: "low", category: "carb", emoji: "🥖" },
  { id: "potato", name: "Baked Potato", gi: 85, giLevel: "high", category: "carb", emoji: "🥔" },
  { id: "sweet-potato", name: "Sweet Potato", gi: 44, giLevel: "low", category: "carb", emoji: "🍠" },
  { id: "oats", name: "Oatmeal", gi: 55, giLevel: "low", category: "carb", emoji: "🥣" },

  // Vegetables
  { id: "broccoli", name: "Broccoli", gi: 10, giLevel: "low", category: "vegetable", emoji: "🥦" },
  { id: "spinach", name: "Spinach", gi: 15, giLevel: "low", category: "vegetable", emoji: "🥬" },
  { id: "carrots", name: "Carrots", gi: 16, giLevel: "low", category: "vegetable", emoji: "🥕" },
  { id: "tomatoes", name: "Tomatoes", gi: 15, giLevel: "low", category: "vegetable", emoji: "🍅" },
  { id: "salad", name: "Mixed Salad", gi: 10, giLevel: "low", category: "vegetable", emoji: "🥗" },

  // Fruits
  { id: "apple", name: "Apple", gi: 36, giLevel: "low", category: "fruit", emoji: "🍎" },
  { id: "banana", name: "Banana", gi: 51, giLevel: "low", category: "fruit", emoji: "🍌" },
  { id: "berries", name: "Berries", gi: 25, giLevel: "low", category: "fruit", emoji: "🍓" },
  { id: "orange", name: "Orange", gi: 43, giLevel: "low", category: "fruit", emoji: "🍊" },

  // Fats & Dairy
  { id: "avocado", name: "Avocado", gi: 10, giLevel: "low", category: "fat", emoji: "🥑" },
  { id: "nuts", name: "Mixed Nuts", gi: 14, giLevel: "low", category: "fat", emoji: "🥜" },
  { id: "cheese", name: "Cheese", gi: 0, giLevel: "low", category: "dairy", emoji: "🧀" },
  { id: "yogurt", name: "Greek Yogurt", gi: 11, giLevel: "low", category: "dairy", emoji: "🥛" },
];

const CATEGORIES = [
  { id: "protein", label: "Protein" },
  { id: "carb", label: "Carbs" },
  { id: "vegetable", label: "Veggies" },
  { id: "fruit", label: "Fruit" },
  { id: "fat", label: "Fats" },
  { id: "dairy", label: "Dairy" },
];

interface PlateViewProps {
  onBack?: () => void;
}

export default function PlateView({ onBack }: PlateViewProps) {
  const { user } = useAuth();
  const { showBadge } = useBadgeToast();
  const [plateItems, setPlateItems] = useState<PlateFood[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("protein");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [dietaryPrefs, setDietaryPrefs] = useState<DietaryPreferences | undefined>(undefined);

  useEffect(() => {
    if (user) {
      getUserStats(user.uid).then((stats) => {
        if (stats.dietType || stats.allergies?.length) {
          setDietaryPrefs({
            dietType: stats.dietType || "none",
            allergies: stats.allergies || [],
          });
        }
      }).catch(console.error);
    }
  }, [user]);

  const addToPlate = (food: PlateFood) => {
    if (!plateItems.find((item) => item.id === food.id)) {
      setPlateItems([...plateItems, food]);
      setAnalysis(null);
    }
  };

  const removeFromPlate = (foodId: string) => {
    setPlateItems(plateItems.filter((item) => item.id !== foodId));
    setAnalysis(null);
  };

  const clearPlate = () => {
    setPlateItems([]);
    setAnalysis(null);
  };

  const calculateCombinedGI = () => {
    if (plateItems.length === 0) return 0;
    const carbItems = plateItems.filter((item) => item.gi > 0);
    if (carbItems.length === 0) return 0;

    // Weighted average with protein/fat reduction
    const carbGISum = carbItems.reduce((sum, item) => sum + item.gi, 0);
    const baseGI = carbGISum / carbItems.length;

    // Protein/fat reduces effective GI by about 10-20%
    const hasProtein = plateItems.some((item) => item.category === "protein");
    const hasFat = plateItems.some((item) => item.category === "fat" || item.category === "dairy");
    const hasFiber = plateItems.some((item) => item.category === "vegetable");

    let reduction = 1;
    if (hasProtein) reduction -= 0.1;
    if (hasFat) reduction -= 0.1;
    if (hasFiber) reduction -= 0.05;

    return Math.round(baseGI * Math.max(reduction, 0.7));
  };

  const getGILevel = (gi: number): "low" | "medium" | "high" => {
    if (gi <= 55) return "low";
    if (gi <= 69) return "medium";
    return "high";
  };

  const getBalanceIndicator = () => {
    const categories = {
      protein: plateItems.filter((item) => item.category === "protein").length,
      carb: plateItems.filter((item) => item.category === "carb").length,
      vegetable: plateItems.filter((item) => item.category === "vegetable").length,
      fruit: plateItems.filter((item) => item.category === "fruit").length,
      fat: plateItems.filter((item) => item.category === "fat" || item.category === "dairy").length,
    };

    const issues: string[] = [];
    if (categories.protein === 0) issues.push("Add protein to slow glucose absorption");
    if (categories.vegetable === 0 && categories.fruit === 0) issues.push("Add vegetables for fiber");
    if (categories.carb > 2) issues.push("Consider reducing carb portions");

    return {
      hasProtein: categories.protein > 0,
      hasVeggies: categories.vegetable > 0 || categories.fruit > 0,
      hasFat: categories.fat > 0,
      isBalanced: issues.length === 0,
      issues,
    };
  };

  const handleAnalyze = async () => {
    if (plateItems.length === 0) return;

    setIsAnalyzing(true);
    setAnalysis(null);

    try {
      const foods = plateItems.map((item) => item.name);
      const result = await analyzeMeal(foods, dietaryPrefs);

      // Parse OVERALL_GI from response
      const giMatch = result.match(/OVERALL_GI:(low|medium|high)/i);
      const isLowGi = giMatch?.[1]?.toLowerCase() === "low";
      const cleanedResult = result.replace(/OVERALL_GI:(low|medium|high)\n?/i, "");

      setAnalysis(cleanedResult);

      // Record stats and check badges
      if (user) {
        await recordMealAnalysis(user.uid);
        const lowGiBadge = await checkLowGiChampion(user.uid, isLowGi);
        if (lowGiBadge) showBadge(lowGiBadge);

        const stats = await getUserStats(user.uid);
        const newBadges = await checkAndAwardBadges(user.uid, stats);
        newBadges.forEach((badge) => showBadge(badge));
      }
    } catch (error) {
      console.error("Error analyzing plate:", error);
      setAnalysis("Sorry, I had trouble analyzing your plate. Please try again.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const combinedGI = calculateCombinedGI();
  const giLevel = getGILevel(combinedGI);
  const balance = getBalanceIndicator();
  const filteredFoods = FOOD_OPTIONS.filter((food) => food.category === selectedCategory);

  const renderGIBadge = (text: string) => {
    const match = text.match(/\{\{gi:(\d+):(\w+)\}\}/);
    if (match) {
      const [, value, level] = match;
      return (
        <span className={`gi-pill gi-pill--${level || "medium"}`}>
          <span className="font-bold">{value}</span>
          {" "}
          <span className="opacity-75">{level === "low" ? "Low" : level === "medium" ? "Med" : "High"}</span>
        </span>
      );
    }
    return <code className="bg-[#f0ece6] text-text px-1.5 py-0.5 rounded text-xs">{text}</code>;
  };

  return (
    <div className="p-4">
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-muted font-[family-name:--font-ui] mb-4 hover:text-text"
        >
          <BackIcon className="w-4 h-4" /> Back
        </button>
      )}

      <div className="section-label">Build Your Plate</div>
      <p className="text-sm text-muted mb-6">Add foods to see their combined glycemic impact.</p>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            className={`px-3 py-1.5 rounded-md text-sm font-[family-name:--font-ui] transition-colors ${
              selectedCategory === cat.id
                ? "bg-accent text-white"
                : "border border-border text-text hover:bg-black/5"
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Food Grid */}
      <div className="space-y-2 mb-6">
        {filteredFoods.map((food) => {
          const isOnPlate = plateItems.some((item) => item.id === food.id);
          return (
            <button
              key={food.id}
              onClick={() => (isOnPlate ? removeFromPlate(food.id) : addToPlate(food))}
              className={`w-full p-3 rounded-md text-left transition-colors ${
                isOnPlate
                  ? "border border-accent bg-accent/5"
                  : "border border-border hover:bg-black/5"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">{food.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-text truncate">{food.name}</p>
                </div>
                <span className={`gi-pill gi-pill--${food.giLevel}`}>
                  GI {food.gi}
                </span>
                {isOnPlate && (
                  <span className="text-accent text-sm font-[family-name:--font-ui]">Added</span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected Items */}
      {plateItems.length > 0 && (
        <div className="border-t border-border pt-4 mb-4">
          <div className="section-label">On Your Plate ({plateItems.length})</div>
          <div className="space-y-1 mb-4">
            {plateItems.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between py-1.5 text-sm text-text"
              >
                <span>{item.emoji} {item.name}</span>
                <button
                  onClick={() => removeFromPlate(item.id)}
                  className="text-muted hover:text-text text-xs"
                >
                  &times;
                </button>
              </div>
            ))}
          </div>

          {/* Combined GI Indicator */}
          <div className="flex items-center gap-3 mb-4">
            <span className="text-sm text-muted font-[family-name:--font-ui]">Combined GI Estimate</span>
            <span className={`gi-pill gi-pill--${giLevel}`}>
              {combinedGI} {giLevel === "low" ? "Low" : giLevel === "medium" ? "Medium" : "High"}
            </span>
          </div>

          {/* Balance Indicator */}
          <div className="mb-4">
            {balance.isBalanced ? (
              <p className="text-sm text-gi-low">
                Well balanced -- your meal has protein, fiber, and balanced carbs.
              </p>
            ) : (
              <ul className="space-y-1">
                {balance.issues.map((issue, i) => (
                  <li key={i} className="text-sm text-muted">
                    {issue}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Analyze Button */}
          <button
            onClick={handleAnalyze}
            disabled={isAnalyzing}
            className="w-full py-3 rounded-md bg-accent text-white font-[family-name:--font-ui] hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isAnalyzing ? (
              <>
                <div className="spinner spinner--sm"></div>
                Analyzing...
              </>
            ) : (
              "Get Detailed Analysis"
            )}
          </button>

          {/* Clear Plate */}
          <button
            onClick={clearPlate}
            className="w-full mt-2 py-2 text-sm text-muted hover:text-text font-[family-name:--font-ui]"
          >
            Clear Plate
          </button>
        </div>
      )}

      {/* Analysis Results */}
      {analysis && (
        <div className="border-t border-border pt-4 mt-4">
          <div className="section-label">AI Analysis</div>
          <div className="prose prose-sm prose-tufte max-w-none prose-p:my-2 prose-ul:my-2 prose-li:my-0.5">
            <ReactMarkdown
              components={{
                code: ({ children }) => renderGIBadge(String(children)),
              }}
            >
              {analysis}
            </ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
}
